import { getErrorMessage } from "api/errors";
import { useWebhooks } from "api/webhooks";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  DynamicSearch,
  DynamicSearchType,
} from "components/FiltersV2/DynamicSearch";
import { FiltersV2 } from "components/FiltersV2/Filters";
import {
  getNewFiltersOnly,
  parseFiltersForBE,
} from "components/FiltersV2/utils";
import { Subheader } from "components/Header/Subheader/Subheader";
import { SubheaderLinkButton } from "components/Header/Subheader/SubheaderLinkButton";
import { SubheaderWrapper } from "components/Header/Subheader/SubheaderWrapper";
import { TopHeader } from "components/Header/TopHeader/TopHeader";
import { ReactComponent as SettingsIcon } from "components/Icons/settings-01.svg";
import { createHeadItems, OLTable, Row } from "components/Table/OLTable";
import { calculateCount } from "components/Table/tableHelpers";
import { ConfirmationModal } from "components/Tooltips/ConfirmationModal";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { clearTablePage, selectTables } from "reduxState/tables/tablesSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { WEBHOOK_LIST_ID } from "state/tables/types";
import {
  SelectedFilter,
  SelectedFilter as SelectedFilterV2,
} from "types/filters";
import { ListingActions } from "types/listing";
import { WebhookListItem } from "types/webhooks";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";

const CreateItems = (items: WebhookListItem[]): Row[] => {
  return items.map((item) => ({
    cells: [
      { content: item.eventName, property: "eventName" },
      { content: OLDate(item.createdAt), property: "createdAt" },
      { content: item.url, property: "url" },
    ],
    id: item.webhookSubscriptionId!,
  }));
};

export function WebhookList() {
  const { message } = useMessage();
  const navigate = useNavigate();
  const appDispatch = useAppDispatch();
  const { notify } = useNotifications();
  const { useWebhookList, removeWebhook } = useWebhooks();
  const { listing, updateListing } = useListing(WEBHOOK_LIST_ID);
  const tables = useAppSelector(selectTables);
  const {
    setPersistentFilters,
    setPersistentListingSorting,
    setColumnOrdering,
  } = useTablesStateHelpers();

  const [selectedWebhook, setSelectedWebhook] = useState("");
  const [open, setOpen] = useState(false);
  const [dynamicSearch, setDynamicSearch] = useState<DynamicSearchType>();
  const [filters, setFilters] = useState<SelectedFilter[]>(
    tables[WEBHOOK_LIST_ID]?.filters?.filter(
      (item: SelectedFilter) => item.type !== "dynamic-search",
    ) || [],
  );

  const { data, error } = useWebhookList({
    filters: parseFiltersForBE(getNewFiltersOnly(filters)),
    order: listing.order || "desc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "createdAt",
    dynamicSearch,
  });

  const headItems = createHeadItems([
    {
      headerText: message({ id: "webhookName" }),
      property: "eventName",
      isSortable: true,
    },
    {
      headerText: message({ id: "activeFrom" }),
      property: "createdAt",
      isSortable: true,
    },
    {
      headerText: message({ id: "url" }),
      property: "url",
      isSortable: true,
    },
  ]);

  const items = data?.items ? CreateItems(data.items) : [];

  const setSelectedFilters = (filters: SelectedFilterV2[]) => {
    listing.page = 1;
    setPersistentListingSorting(
      listing.order,
      listing.property,
      listing.rowsPerPage,
      1,
      WEBHOOK_LIST_ID,
    );

    setFilters(filters);
  };

  useEffect(() => {
    const isError = typeof error !== "undefined";
    // Save filters only when data is correct and error not occurs
    if (!isError && data) {
      setPersistentFilters(filters, WEBHOOK_LIST_ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, data, error]);

  return (
    <>
      <TopHeader title={message({ id: "activeWebhooks" })} />
      <Subheader>
        <FiltersV2
          data-test-id="webhooks"
          donutLabel={message({ id: "webhooks" })}
          properties={[
            {
              query: "createdAt",
              label: message({ id: "activeFrom" }),
              type: "datetime",
            },
            {
              query: "url",
              label: message({ id: "url" }),
              type: "text",
            },
            {
              query: "eventName",
              label: message({ id: "webhookName" }),
              type: "webhook-name-filter",
            },
          ]}
          setFilters={setSelectedFilters}
          savedFilters={getNewFiltersOnly(filters)}
          total={data?.total}
        />
        <SubheaderWrapper>
          <SubheaderLinkButton
            text={message({ id: "expiringNotifications" })}
            icon={<SettingsIcon />}
            link={links.communication}
            variant="outlined"
            data-test-id="webhook-expiring-notifications-button"
          />
          <SubheaderLinkButton
            text={message({ id: "addWebhook" })}
            link={links.addWebhook}
            data-test-id="webhooks-add-new-webhook-button"
          />
        </SubheaderWrapper>
      </Subheader>
      <Subheader>
        <SubheaderWrapper alignRight>
          <DynamicSearch
            property="eventName"
            setDynamicSearch={setDynamicSearch}
            onSearch={() =>
              appDispatch(clearTablePage({ tableName: WEBHOOK_LIST_ID }))
            }
          />
        </SubheaderWrapper>
      </Subheader>
      <OLTable
        fullView
        setColumnOrdering={setColumnOrdering}
        tableName={WEBHOOK_LIST_ID}
        sort={{
          order: listing.order || "desc",
          property: listing.property || "createdAt",
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
        actions={{
          body: [
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setSelectedWebhook(id as string);
                setOpen(true);
              },
              label: message({ id: "delete" }),
            },
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => navigate(links.editWebhook(id as string)),
              label: message({ id: "edit" }),
            },
          ],
        }}
        columns={data && headItems}
        data-test-id="webhooks"
        onRowClick={(id) => navigate(links.editWebhook(id))}
        onSort={(prop) =>
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          })
        }
        pagination={{
          count: calculateCount(data?.total.filtered, data?.total.all),
          onChangePage: (page) =>
            updateListing({ payload: page, type: ListingActions.CHANGE_PAGE }),
          onChangeRowsPerPage: (rows) =>
            updateListing({
              payload: rows,
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
            }),
          page: listing.page,
          rowsPerPage: listing.rowsPerPage,
        }}
        rows={data && items}
        clearFilters={() => setFilters([])}
      />
      <ConfirmationModal
        action={async () => {
          try {
            const response = await removeWebhook(selectedWebhook);
            if (response) {
              setOpen(false);
              notify(message({ id: "webhookRemoved" }));
            }
          } catch (error) {
            notify(getErrorMessage(error));
          }
        }}
        close={() => setOpen(false)}
        header="deleteHeader"
        info="deleteWebhookInfo"
        open={open}
      />
    </>
  );
}
